import { useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  TextField,
  Container,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import useSparks from '../hooks/useSparks';
import { useSquircle } from '../hooks/useSquircle';

const CARD_BG = '#fffeed';
const DARK_BG = '#1a1a2e';
const CHIP_SELECTED = '#fffeed';
const CHIP_UNSELECTED = '#c3c3d4';

const ROBOTO_SERIF_SETTINGS = "'GRAD' 0, 'wdth' 100";
const ROBOTO_FLEX_BLACK_SETTINGS =
  "'GRAD' 0, 'XOPQ' 96, 'XTRA' 468, 'YOPQ' 79, 'YTAS' 750, 'YTDE' -203, 'YTFI' 738, 'YTLC' 514, 'YTUC' 712, 'wdth' 151";


// Repeating editorial layout pattern — every 6 cards cycles through
// the same rhythm of small / wide / tall cards as seen in the mockup.
const CARD_PATTERN = [
  'small', // 0 — half width
  'small', // 1 — half width
  'wide',  // 2 — full width, large black italic
  'small', // 3 — half width
  'tall',  // 4 — half width, double height, large black italic
  'small', // 5 — half width
];

function getCardVariant(index) {
  return CARD_PATTERN[index % CARD_PATTERN.length];
}

function SparkTile({ spark, variant = 'small', onClick }) {
  const momentText = spark.moment || spark.trigger || '';
  const isIgnited = (spark.intensity ?? spark.rating ?? null) === 3;
  const isFeatured = variant === 'wide' || variant === 'tall';
  const { ref, squircleStyle } = useSquircle({ cornerRadius: 20, cornerSmoothing: 1 });

  return (
    <Box
      ref={ref}
      onClick={onClick}
      style={squircleStyle}
      sx={{
        // Grid placement — wide spans both columns, tall spans 2 rows
        gridColumn: variant === 'wide' ? '1 / span 2' : 'auto',
        gridRow: variant === 'tall' ? 'auto / span 2' : 'auto',
        px: 2,
        pt: 2,
        pb: 1.5,
        bgcolor: CARD_BG,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        cursor: 'pointer',
        ...(isIgnited && {
          bgcolor: '#ff371d',
        }),
        transition: 'opacity 0.15s ease',
        '&:active': { opacity: 0.85 },
      }}
    >
      <p
        style={{
          fontFamily: '"Roboto Serif", serif',
          // Featured (wide/tall): Black Italic at 24px. Small: Medium at 14px.
          fontWeight: isFeatured ? 900 : 500,
          fontStyle: isFeatured ? 'italic' : 'normal',
          fontSize: isFeatured ? '1.5rem' : '0.875rem',
          lineHeight: isFeatured ? 'normal' : 1.3,
          color: isIgnited ? '#fffeed' : DARK_BG,
          margin: 0,
          fontVariationSettings: ROBOTO_SERIF_SETTINGS,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: isFeatured ? 6 : 4,
        }}
      >
        {momentText}
      </p>
    </Box>
  );
}

function HomeScreen() {
  const navigate = useNavigate();
  const { sparks, loading, addSpark } = useSparks();
  const [momentInput, setMomentInput] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const scrollRef = useRef(null);

  const handleCreateSpark = useCallback(() => {
    if (!momentInput.trim()) return;
    const newSpark = addSpark({
      moment: momentInput.trim(),
      why: null,
      intensity: 2,
      tellingYou: null,
      compelledTo: null,
      timeframe: null,
      completed: false,
    });
    setMomentInput('');
    navigate(`/edit/${newSpark.id}`);
  }, [momentInput, addSpark, navigate]);

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'intensity-3', label: 'Ignited' },
  ];

  const filteredSparks = useMemo(() => {
    let result = [...sparks];
    if (selectedFilter !== 'all') {
      if (selectedFilter.startsWith('intensity-')) {
        const intensityValue = Number(selectedFilter.split('-')[1]);
        result = result.filter(
          (s) => (s.intensity ?? s.rating ?? null) === intensityValue
        );
      }
    }
    result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return result;
  }, [sparks, selectedFilter]);

  const { ref: createCardRef, squircleStyle: createCardSquircle } = useSquircle({
    cornerRadius: 36,
    cornerSmoothing: 1,
  });

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ color: CARD_BG }}>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box ref={scrollRef}>
      <Container maxWidth="sm" disableGutters>
        <Box sx={{ px: 2, pt: 1, pb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Hero — headline over the dark bg (star arm handled by App.js panel layer) */}
            <Box sx={{ pt: 1, pb: 1 }}>
              <p
                style={{
                  fontFamily: '"Roboto Flex", sans-serif',
                  fontWeight: 900,
                  fontSize: 'clamp(32px, 11vw, 48px)',
                  lineHeight: 1,
                  // Red-orange to match Figma update
                  color: '#ff371d',
                  textTransform: 'uppercase',
                  textAlign: 'justify',
                  margin: 0,
                  fontVariationSettings: ROBOTO_FLEX_BLACK_SETTINGS,
                }}
              >
                FEELING<br />INSPIRED<br />TODAY?
              </p>
            </Box>

            {/* Create spark card — true Figma squircle via clip-path */}
            <Box
              ref={createCardRef}
              style={createCardSquircle}
              sx={{
                position: 'relative',
                zIndex: 1,
                bgcolor: CARD_BG,
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                placeholder="What inspired you?"
                value={momentInput}
                onChange={(e) => setMomentInput(e.target.value)}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: '1.125rem',
                    lineHeight: 1.6,
                    p: 0,
                    fontFamily: '"Roboto Serif", serif',
                    fontVariationSettings: ROBOTO_SERIF_SETTINGS,
                    color: DARK_BG,
                  },
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    bgcolor: 'transparent',
                    p: 0,
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(26,26,46,0.45)',
                    opacity: 1,
                  },
                }}
              />

              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleCreateSpark}
                disabled={!momentInput.trim()}
                fullWidth
                sx={{
                  py: 1.5,
                  borderRadius: '100px',
                  bgcolor: momentInput.trim() ? '#ff371d' : 'rgba(26,26,46,0.1)',
                  color: momentInput.trim() ? CARD_BG : 'rgba(26,26,46,0.38)',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: momentInput.trim() ? '#cc2a12' : 'rgba(26,26,46,0.1)',
                    boxShadow: 'none',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(26,26,46,0.1)',
                    color: 'rgba(26,26,46,0.38)',
                  },
                }}
              >
                Create spark
              </Button>
            </Box>

            {/* Filter chips */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {filters.map((filter) => {
                const isSelected = selectedFilter === filter.key;
                return (
                  <Chip
                    key={filter.key}
                    label={filter.label}
                    onClick={() => setSelectedFilter(filter.key)}
                    sx={{
                      height: 38,
                      fontSize: '0.875rem',
                      fontWeight: isSelected ? 600 : 400,
                      bgcolor: isSelected ? CHIP_SELECTED : CHIP_UNSELECTED,
                      color: DARK_BG,
                      border: 'none',
                      borderRadius: '36px',
                      flexShrink: 0,
                      '&:hover': {
                        bgcolor: isSelected
                          ? CHIP_SELECTED
                          : 'rgba(195,195,212,0.8)',
                      },
                      '& .MuiChip-label': { px: 1.5 },
                    }}
                  />
                );
              })}
            </Box>

            {/* Spark grid */}
            {filteredSparks.length > 0 ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  // Each row is 116px — tall cards span 2 rows → 116 + 16 (gap) + 116 = 248px
                  gridAutoRows: 116,
                  // Dense packing fills the gap left beside a tall card
                  gridAutoFlow: 'dense',
                  gap: 2,
                }}
              >
                {filteredSparks.map((spark, index) => (
                  <SparkTile
                    key={spark.id}
                    spark={spark}
                    variant={getCardVariant(index)}
                    onClick={() => navigate(`/spark/${spark.id}`)}
                  />
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  py: 6,
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 48, opacity: 0.2, color: CARD_BG }} />
                <Typography sx={{ color: CHIP_UNSELECTED, textAlign: 'center' }}>
                  {selectedFilter !== 'all'
                    ? 'No sparks match your filter'
                    : 'No sparks yet — write your first above'}
                </Typography>
              </Box>
            )}

          </Box>
      </Container>
    </Box>
  );
}

export default HomeScreen;
